import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import type { DiaperLinkStatus } from './useDiaperSelection';
import type { Criterion, Phase } from '../lib/types';
import type { ClipFile } from '../lib/api-clip-monitor';

const LOG = (msg: string, ...args: unknown[]) => {
  const serialized = args.map(a => (typeof a === 'object' && a !== null) ? JSON.stringify(a) : a);
  console.log(`[UrlSync] ${msg}`, ...serialized);
};

interface UseInspectorUrlSyncParams {
  availableClips: ClipFile[];
  selectedClipAddress: string | null;
  selectClip: (address: string | null) => void;
  selectedDiaperCounter: number | null;
  currentCriterion: Criterion | null;
  currentPhase: Phase | null;
  diaperLinkStatus: DiaperLinkStatus;
}

export function useInspectorUrlSync(params: UseInspectorUrlSyncParams): void {
  const {
    availableClips,
    selectedClipAddress,
    selectClip,
    selectedDiaperCounter,
    currentCriterion,
    currentPhase,
    diaperLinkStatus,
  } = params;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialParamsAppliedRef = useRef(false);
  const prevClipParamRef = useRef<string | null>(searchParams.get('clip'));
  const clipParamAppliedRef = useRef(false);

  const clipParam = searchParams.get('clip');
  const diaperParam = searchParams.get('diaper');
  const diaperStartParam = searchParams.get('diaperStart');

  // Reset sync gating when clip param changes (new navigation)
  if (clipParam !== prevClipParamRef.current) {
    prevClipParamRef.current = clipParam;
    initialParamsAppliedRef.current = false;
    clipParamAppliedRef.current = false;
  }

  // Apply URL clip param to store — only when clipParam changes or clips load,
  // NOT when the store's selectedClipAddress changes (that would revert manual selections)
  useEffect(() => {
    if (clipParamAppliedRef.current) return;
    LOG('Clip param effect', { clipParam, availableClipsCount: availableClips.length, selectedClipAddress });
    if (!clipParam) return;
    if (availableClips.length === 0) return;
    const exists = availableClips.some(c => c.clipAddress === clipParam);
    if (exists) {
      clipParamAppliedRef.current = true;
      if (selectedClipAddress !== clipParam) {
        LOG('selectClip', clipParam);
        selectClip(clipParam);
      }
    }
  }, [clipParam, availableClips, selectedClipAddress, selectClip]);

  // Sync store state → URL
  useEffect(() => {
    if (!selectedClipAddress) return;

    if (!initialParamsAppliedRef.current) {
      const clipResolved = !clipParam || selectedClipAddress === clipParam;
      const hasDiaperParam = !!(diaperParam || diaperStartParam);
      const diaperResolved = !hasDiaperParam || diaperLinkStatus === 'found' || (diaperLinkStatus !== null && diaperLinkStatus !== 'searching');
      if (clipResolved && diaperResolved) {
        initialParamsAppliedRef.current = true;
        LOG('Initial params applied');
      }
      return;
    }

    const liveClip = searchParams.get('clip');
    const liveDiaper = searchParams.get('diaper');
    const liveCriterion = searchParams.get('criterion');
    const livePhase = searchParams.get('phase');

    const desiredDiaper = selectedDiaperCounter !== null ? String(selectedDiaperCounter) : null;
    const desiredCriterion = currentCriterion ? String(currentCriterion.id) : null;
    const desiredPhase = currentPhase ? String(currentPhase.id) : null;

    const clipMismatch = selectedClipAddress !== liveClip;
    const diaperMismatch = desiredDiaper !== liveDiaper;
    const criterionMismatch = desiredCriterion !== liveCriterion;
    const phaseMismatch = desiredPhase !== livePhase;

    if (clipMismatch || diaperMismatch || criterionMismatch || phaseMismatch) {
      const diaperPart = selectedDiaperCounter !== null ? `&diaper=${selectedDiaperCounter}` : '';
      const criterionPart = currentCriterion ? `&criterion=${currentCriterion.id}` : '';
      const phasePart = currentPhase ? `&phase=${currentPhase.id}` : '';
      LOG('Updating URL', { clipMismatch, diaperMismatch, criterionMismatch, phaseMismatch });
      navigate(`/diaper-inspector?clip=${encodeURIComponent(selectedClipAddress)}${diaperPart}${criterionPart}${phasePart}`, { replace: true });
    }
  }, [selectedClipAddress, selectedDiaperCounter, currentCriterion, currentPhase, searchParams, navigate, diaperLinkStatus, clipParam, diaperParam, diaperStartParam]);
}
